import api from '@/services/api'

export default {
  namespaced: true,

  state: () => ({
    designs:       [],   // list of designer_labels rows
    activeDesign:  null, // currently open label metadata
    legacyLabels:  [],   // labels from the legacy table (for import)
    loading:       false,
    error:         null,
  }),

  getters: {
    designs:      (s) => s.designs,
    activeDesign: (s) => s.activeDesign,
    legacyLabels: (s) => s.legacyLabels,
    loading:      (s) => s.loading,
    error:        (s) => s.error,
  },

  mutations: {
    SET_DESIGNS(state, designs)         { state.designs      = designs },
    SET_ACTIVE_DESIGN(state, design)    { state.activeDesign = design  },
    SET_LEGACY_LABELS(state, labels)    { state.legacyLabels = labels  },
    ADD_DESIGN(state, design) {
      state.designs.unshift(design)
    },
    UPDATE_DESIGN(state, design) {
      const idx = state.designs.findIndex(d => d.designId === design.designId)
      if (idx !== -1) state.designs.splice(idx, 1, design)
      if (state.activeDesign?.designId === design.designId) {
        state.activeDesign = design
      }
    },
    REMOVE_DESIGN(state, designId) {
      state.designs = state.designs.filter(d => d.designId !== designId)
      if (state.activeDesign?.designId === designId) state.activeDesign = null
    },
    SET_LOADING(state, val) { state.loading = val },
    SET_ERROR(state, msg)   { state.error   = msg },
  },

  actions: {
    async fetchDesigns({ commit }, { search = '', limit = 50, offset = 0 } = {}) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.get('/designer', { params: { search, limit, offset } })
        commit('SET_DESIGNS', data.designs)
        return data.designs
      } catch (err) {
        commit('SET_ERROR', err.response?.data?.error || 'Failed to load designs')
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchDesign({ commit, dispatch }, designId) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.get(`/designer/${designId}`)
        // API returns flat object: { designId, labelName, ..., elements: [] }
        const { elements, ...design } = data
        commit('SET_ACTIVE_DESIGN', design)
        // Load elements into canvas store
        await dispatch('canvas/loadElements', elements || [], { root: true })
        return data
      } catch (err) {
        commit('SET_ERROR', err.response?.data?.error || 'Failed to load design')
        return null
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createDesign({ commit }, payload) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.post('/designer', payload)
        commit('ADD_DESIGN', data.design)
        commit('SET_ACTIVE_DESIGN', data.design)
        return { ok: true, design: data.design }
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to create design'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateDesign({ commit }, { designId, ...payload }) {
      try {
        const { data } = await api.put(`/designer/${designId}`, payload)
        commit('UPDATE_DESIGN', data.design)
        return { ok: true, design: data.design }
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to update design'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      }
    },

    async deleteDesign({ commit }, designId) {
      try {
        await api.delete(`/designer/${designId}`)
        commit('REMOVE_DESIGN', designId)
        return { ok: true }
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to delete design'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      }
    },

    async saveElements({ state, rootGetters }, elements) {
      if (!state.activeDesign) return { ok: false, error: 'No active design' }
      try {
        // Map canvas labelX/labelY back to API posX/posY
        const mapped = (elements || []).map(e => ({
          ...e,
          posX: e.labelX ?? e.posX ?? 0,
          posY: e.labelY ?? e.posY ?? 0,
        }))
        const { data } = await api.put(
          `/designer/${state.activeDesign.designId}/elements`,
          { elements: mapped }
        )
        return { ok: true, elements: data.elements }
      } catch (err) {
        return { ok: false, error: err.response?.data?.error || 'Save failed' }
      }
    },

    async fetchLegacyLabels({ commit }, { search = '' } = {}) {
      try {
        const { data } = await api.get('/labels', { params: { search, limit: 100 } })
        commit('SET_LEGACY_LABELS', data.labels)
        return data.labels
      } catch {
        return []
      }
    },

    async copyDesign({ commit }, designId) {
      try {
        const { data } = await api.post(`/designer/${designId}/copy`)
        commit('ADD_DESIGN', data.design)
        return { ok: true, design: data.design }
      } catch (err) {
        return { ok: false, error: err.response?.data?.error || 'Copy failed' }
      }
    },

    async exportToLegacy({ state }) {
      if (!state.activeDesign) return { ok: false, error: 'No active design' }
      try {
        const { data } = await api.post(
          `/designer/${state.activeDesign.designId}/export`
        )
        return { ok: true, legacyLabelId: data.legacyLabelId }
      } catch (err) {
        return { ok: false, error: err.response?.data?.error || 'Export failed' }
      }
    },
  },
}